import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { useAllUserProjects } from '@/shared/hooks/useUserProjects';
import {
  ArrowLeft,
  Download,
  Paperclip,
  PlusCircle,
  Printer,
  FileText,
  ListChecks,
  CheckCircle2,
  Layers,
  History,
  ClipboardList,
  Calendar,
  CheckCircle,
  Clock,
  Circle,
  AlertCircle,
  RefreshCw,
  MoreHorizontal,
  Pencil,
} from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Textarea } from '@/shared/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { SubmittalFormDialog } from './SubmittalFormDialog';
import { useProjectUsers, useSubmittal, useSubmittalMutations, useSubmittalRevisions } from '../hooks';
import { SUBMITTALS_DETAIL_TOUR_ID } from '@/features/tours/submittals/detail';
import { SUBMITTALS_COMBINED_TOUR_ID } from '@/features/tours/submittals/combined';
import { useTour } from '@/features/tours';
import {
  SUBMITTAL_PRIORITY_LABELS,
  SUBMITTAL_RESPONSE_LABELS,
  SUBMITTAL_STATUS_LABELS,
  SUBMITTAL_TYPE_LABELS,
  type SubmittalResponseCode,
  type SubmittalStatus,
} from '../types';
import { getSubmittalsExportCsvUrl } from '../api/api';
import { formatDate, formatDateTime } from '../utils/format';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const STATUS_BADGE: Record<SubmittalStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  OPEN: 'bg-blue-100 text-blue-800',
  IN_REVIEW: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-green-100 text-green-800',
  APPROVED_WITH_COMMENTS: 'bg-green-50 text-green-700',
  REVISE_RESUBMIT: 'bg-orange-100 text-orange-800',
  REJECTED: 'bg-red-100 text-red-800',
  CLOSED: 'bg-slate-100 text-slate-700',
};

const PRIORITY_BADGE: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-700',
  MEDIUM: 'bg-blue-100 text-blue-800',
  HIGH: 'bg-orange-100 text-orange-800',
  CRITICAL: 'bg-red-100 text-red-800',
};

const STATUS_BANNER: Record<
  SubmittalStatus,
  { wrap: string; icon: string; text: string; Icon: typeof CheckCircle }
> = {
  DRAFT: { wrap: 'border-gray-200 bg-gray-50', icon: 'text-gray-600', text: 'text-gray-800', Icon: FileText },
  OPEN: { wrap: 'border-blue-200 bg-blue-50', icon: 'text-blue-600', text: 'text-blue-800', Icon: Circle },
  IN_REVIEW: { wrap: 'border-amber-200 bg-amber-50', icon: 'text-amber-600', text: 'text-amber-800', Icon: Clock },
  APPROVED: {
    wrap: 'border-green-200 bg-green-50',
    icon: 'text-green-600',
    text: 'text-green-800',
    Icon: CheckCircle,
  },
  APPROVED_WITH_COMMENTS: {
    wrap: 'border-green-200 bg-green-50',
    icon: 'text-green-600',
    text: 'text-green-800',
    Icon: CheckCircle,
  },
  REVISE_RESUBMIT: {
    wrap: 'border-orange-200 bg-orange-50',
    icon: 'text-orange-600',
    text: 'text-orange-800',
    Icon: RefreshCw,
  },
  REJECTED: { wrap: 'border-red-200 bg-red-50', icon: 'text-red-600', text: 'text-red-800', Icon: AlertCircle },
  CLOSED: { wrap: 'border-slate-200 bg-slate-50', icon: 'text-slate-600', text: 'text-slate-800', Icon: CheckCircle },
};

const DEFAULT_CLOSE_CODE_BY_STATUS: Partial<Record<SubmittalStatus, SubmittalResponseCode>> = {
  APPROVED: 'APPROVED',
  APPROVED_WITH_COMMENTS: 'APPROVED_WITH_COMMENTS',
  REVISE_RESUBMIT: 'REVISE_RESUBMIT',
  REJECTED: 'REJECTED',
};

const userFullName = (u: { first_name: string; last_name: string } | null | undefined) => {
  if (!u) return '—';
  return `${u.first_name} ${u.last_name}`.trim() || '—';
};

export const SubmittalDetail: React.FC = () => {
  const { id, clientName } = useParams<{ id: string; clientName?: string }>();
  const { projectIdNumber: numericProjectId } = useCurrentProject();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.pathname.replace(/\/[^/]+$/, '');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: item, isLoading } = useSubmittal(id, numericProjectId);
  const { data: revisions = [] } = useSubmittalRevisions(id);
  const { data: users = [] } = useProjectUsers(numericProjectId);
  const { allProjects } = useAllUserProjects(clientName);
  const projectName = allProjects.find((p) => p.id === numericProjectId)?.name ?? '';
  const { submitItem, respondItem, closeItem, createRevision, uploadAttachment, deleteAttachment } =
    useSubmittalMutations(numericProjectId);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [responseCode, setResponseCode] = useState<SubmittalResponseCode>('APPROVED');
  const [reviewerAssignmentId, setReviewerAssignmentId] = useState<string>('');
  const [responseComment, setResponseComment] = useState('');
  const [closeCode, setCloseCode] = useState<SubmittalResponseCode>('APPROVED');
  const [closeComment, setCloseComment] = useState('');
  const [distributionIds, setDistributionIds] = useState<number[]>([]);

  const currentStep = item?.workflow_steps.find((step) => !step.is_completed);
  const pendingReviewers = currentStep?.reviewers.filter((r) => !r.has_responded) ?? [];

  useEffect(() => {
    if (!item) return;
    if (pendingReviewers.length === 0) {
      setReviewerAssignmentId('');
      return;
    }
    if (!pendingReviewers.some((reviewer) => reviewer.id === reviewerAssignmentId)) {
      setReviewerAssignmentId(pendingReviewers[0].id);
    }
  }, [item, reviewerAssignmentId, pendingReviewers]);

  useEffect(() => {
    if (!item) return;
    setDistributionIds(item.distribution_recipients.map((recipient) => recipient.id));
  }, [item]);

  useEffect(() => {
    if (!item) return;
    const statusDefault = DEFAULT_CLOSE_CODE_BY_STATUS[item.status] ?? 'APPROVED';
    setCloseCode((item.final_response_code as SubmittalResponseCode) || statusDefault);
    setCloseComment(item.close_comments || '');
  }, [item]);

  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (!item) return;
    if (hasSeen(SUBMITTALS_COMBINED_TOUR_ID)) return;
    if (hasSeen(SUBMITTALS_DETAIL_TOUR_ID)) return;
    void startTour(SUBMITTALS_DETAIL_TOUR_ID);
  }, [tourHydrated, active, startTour, hasSeen, item]);

  if (isLoading || !item) {
    return (
      <BaseLayout>
        <div className="p-6 max-w-6xl mx-auto text-muted-foreground">Loading submittal...</div>
      </BaseLayout>
    );
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    await uploadAttachment.mutateAsync({ id, file });
    e.target.value = '';
  };

  const handlePrint = () => {
    const origin = window.location.origin;

    const fields: [string, string][] = [
      ['Status', SUBMITTAL_STATUS_LABELS[item.status]],
      ['Priority', SUBMITTAL_PRIORITY_LABELS[item.priority]],
      ['Type', SUBMITTAL_TYPE_LABELS[item.submittal_type]],
      ['Package', item.package?.package_id || 'Unpackaged'],
      ['Revision', `Rev ${item.revision_number}`],
      ['Spec Section', item.specification_section || '—'],
      ['Discipline', item.discipline || '—'],
      ['Contractor', item.responsible_contractor || '—'],
      ['Submitted By', userFullName(item.submitted_by)],
      ['Submittal Manager', userFullName(item.submittal_manager)],
      ['Current Reviewer', userFullName(item.ball_in_court_user)],
      ['Trade', item.trade?.name ?? '—'],
      ['Cost Code', item.cost_code ? `${item.cost_code.code} – ${item.cost_code.name}` : '—'],
      ['Received From', item.received_from || '—'],
      ['Received Date', formatDate(item.received_date)],
      ['Required Response', formatDate(item.required_response_date)],
      ['Days Open', String(item.days_open)],
      ['Created At', formatDateTime(item.created_at)],
    ];

    const metaHtml = fields
      .map(
        ([label, value]) =>
          `<div><span class="label">${escapeHtml(label)}:</span> ${escapeHtml(value)}</div>`,
      )
      .join('');

    const workflowHtml = item.workflow_steps.length
      ? `<div class="section"><h2>Workflow</h2>${item.workflow_steps
          .map((step) => {
            const reviewers = step.reviewers
              .map(
                (r) =>
                  `<p>• ${escapeHtml(
                    r.reviewer ? userFullName(r.reviewer) : r.reviewer_role || 'Reviewer',
                  )} — ${r.has_responded ? `Responded ${escapeHtml(formatDateTime(r.responded_at))}` : 'Pending'}${
                    r.responses && r.responses.length
                      ? ` (${escapeHtml(
                          r.responses
                            .map((resp) => SUBMITTAL_RESPONSE_LABELS[resp.response_code])
                            .join(', '),
                        )})`
                      : ''
                  }</p>`,
              )
              .join('');
            return `<p><strong>Step ${step.step_number}${step.title ? ` — ${escapeHtml(step.title)}` : ''}</strong> ${
              step.is_completed ? '<span class="muted">(Completed)</span>' : '<span class="muted">(In progress)</span>'
            }</p>${reviewers}`;
          })
          .join('')}</div>`
      : '';

    const historyHtml = item.status_history.length
      ? `<div class="section"><h2>Status History</h2>${item.status_history
          .map(
            (entry) =>
              `<p><strong>${escapeHtml(entry.from_status || 'Created')} → ${escapeHtml(
                entry.to_status,
              )}</strong> <span class="muted">${escapeHtml(formatDateTime(entry.created_at))}</span>${
                entry.note ? `<br/>${escapeHtml(entry.note)}` : ''
              }</p>`,
          )
          .join('')}</div>`
      : '';

    const attachmentsHtml = item.attachments.length
      ? `<div class="section"><h2>Attachments (${item.attachments.length})</h2><ul>${item.attachments
          .map((att) => `<li>${escapeHtml(att.file.split('/').pop() ?? att.file)}</li>`)
          .join('')}</ul></div>`
      : '';

    const closeHtml = item.close_comments
      ? `<div class="section"><h2>Closing Comments</h2><p>${escapeHtml(item.close_comments)}</p></div>`
      : '';

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(item.submittal_number)} ${escapeHtml(item.title)}</title>
<style>
  @page { size: auto; margin: 0.6in; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; }
  .header { position: relative; text-align: center; border-bottom: 2px solid #111827; padding-bottom: 12px; margin-bottom: 18px; }
  .header .logo { position: absolute; top: 0; right: 0; height: 40px; width: auto; }
  .header .project { font-size: 13px; font-weight: 600; color: #374151; margin: 0 0 2px; }
  .header h1 { font-size: 24px; font-weight: 700; margin: 0 0 4px; }
  .header .subtitle { font-size: 12px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #6b7280; }
  .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 24px; font-size: 12px; margin-bottom: 20px; }
  .meta .label { color: #6b7280; font-weight: 600; }
  .section { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 14px; margin-bottom: 12px; break-inside: avoid; page-break-inside: avoid; }
  .section h2 { font-size: 14px; font-weight: 700; margin: 0 0 8px; }
  .section p { font-size: 12px; margin: 0 0 6px; white-space: pre-wrap; }
  .section p:last-child { margin-bottom: 0; }
  .section .muted { color: #6b7280; font-weight: 400; }
  .section ul { margin: 0; padding-left: 18px; }
  .section li { font-size: 12px; }
</style>
</head>
<body>
  <div class="header">
    <img class="logo" src="${origin}/static/images/iv360-p-300.webp" alt="Ironview 360" />
    ${projectName ? `<div class="project">${escapeHtml(projectName)}</div>` : ''}
    <h1>${escapeHtml(item.title)}</h1>
    <div class="subtitle">Submittal ${escapeHtml(item.submittal_number)} &middot; Rev ${item.revision_number} &middot; ${escapeHtml(SUBMITTAL_STATUS_LABELS[item.status])}</div>
  </div>
  <div class="meta">${metaHtml}</div>
  <div class="section"><h2>Description</h2><p>${escapeHtml(item.description || 'No description provided.')}</p></div>
  ${workflowHtml}
  ${closeHtml}
  ${historyHtml}
  ${attachmentsHtml}
  <script>
    window.onload = function () { window.focus(); window.print(); };
    window.onafterprint = function () { window.close(); };
  </script>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=1200');
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const banner = STATUS_BANNER[item.status];
  const BannerIcon = banner.Icon;
  const bannerMessage =
    item.status === 'DRAFT'
      ? 'Draft — not yet submitted'
      : item.status === 'CLOSED'
        ? `Closed${item.close_date ? ` on ${formatDate(item.close_date)}` : ''}`
        : item.status === 'APPROVED' || item.status === 'APPROVED_WITH_COMMENTS'
          ? SUBMITTAL_STATUS_LABELS[item.status]
          : item.status === 'REJECTED'
            ? 'Rejected'
            : item.status === 'REVISE_RESUBMIT'
              ? 'Revise & resubmit requested'
              : `${SUBMITTAL_STATUS_LABELS[item.status]}${item.required_response_date ? ` — response due ${formatDate(item.required_response_date)}` : ''}`;

  return (
    <BaseLayout>
      <div className="mx-auto w-full min-w-0 max-w-6xl space-y-4 break-words p-4 sm:space-y-6 md:p-6">
        {/* ── Header ────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <Button variant="outline" size="icon" className="flex-shrink-0" onClick={() => navigate(backPath)} aria-label="Back to submittals">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <div className="flex min-w-0 flex-col items-start gap-0.5 sm:flex-row sm:gap-2">
                <span className="shrink-0 font-mono text-sm text-muted-foreground">{item.submittal_number}</span>
                <h1 className="min-w-0 break-words text-2xl font-bold tracking-tight md:text-3xl lg:truncate">
                  {item.title}
                </h1>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={PRIORITY_BADGE[item.priority]}>
                  {SUBMITTAL_PRIORITY_LABELS[item.priority]} Priority
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Revision {item.revision_number} • Created {formatDateTime(item.created_at)}
                </span>
              </div>
            </div>
          </div>

          <div className="w-full flex-shrink-0 lg:w-auto" data-tour-id="submittal-detail-header-actions">
            <div className="hidden flex-wrap items-center gap-2 lg:flex">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handlePrint}>
                <Printer className="h-4 w-4" />
                Print PDF
              </Button>
              <Button variant="outline" size="sm" onClick={() => setIsEditOpen(true)} disabled={!item.can_edit}>
                Edit
              </Button>
              {item.status === 'DRAFT' && (
                <Button size="sm" onClick={() => id && submitItem.mutate(id)}>Open Submittal</Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  if (!id) return;
                  const created = await createRevision.mutateAsync({
                    id,
                    title: `${item.title} (Rev ${item.revision_number + 1})`,
                  });
                  navigate(`${backPath}/${created.id}`);
                }}
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                New Revision
              </Button>
              {numericProjectId && (
                <Button asChild variant="outline" size="sm">
                  <a href={getSubmittalsExportCsvUrl(numericProjectId)} target="_blank" rel="noreferrer">
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </a>
                </Button>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-10 w-full gap-2 lg:hidden">
                  <MoreHorizontal className="h-4 w-4" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 max-w-[calc(100vw-2rem)]">
                <DropdownMenuItem onSelect={handlePrint}>
                  <Printer className="mr-2 h-4 w-4" />
                  Print PDF
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setIsEditOpen(true)} disabled={!item.can_edit}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit submittal
                </DropdownMenuItem>
                {numericProjectId && (
                  <DropdownMenuItem asChild>
                    <a href={getSubmittalsExportCsvUrl(numericProjectId)} target="_blank" rel="noreferrer">
                      <Download className="mr-2 h-4 w-4" />
                      Export CSV
                    </a>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {item.status === 'DRAFT' && (
                  <DropdownMenuItem onSelect={() => id && submitItem.mutate(id)}>
                    <Circle className="mr-2 h-4 w-4" />
                    Open submittal
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={async () => {
                    if (!id) return;
                    const created = await createRevision.mutateAsync({
                      id,
                      title: `${item.title} (Rev ${item.revision_number + 1})`,
                    });
                    navigate(`${backPath}/${created.id}`);
                  }}
                >
                  <PlusCircle className="mr-2 h-4 w-4" />
                  New revision
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* ── Status banner ─────────────────────────────────────────── */}
        <Card className={banner.wrap}>
          <CardContent className="p-4 sm:p-6 sm:py-4">
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-2 sm:items-center">
                <BannerIcon className={`h-5 w-5 shrink-0 ${banner.icon}`} />
                <span className={`min-w-0 break-words text-sm font-medium ${banner.text}`}>{bannerMessage}</span>
              </div>
              <div className="flex max-w-full flex-wrap items-center gap-2">
                <span className="hidden text-xs text-muted-foreground sm:inline">
                  {item.days_open} day{item.days_open === 1 ? '' : 's'} open
                </span>
                <Badge className={`max-w-full whitespace-normal ${STATUS_BADGE[item.status]}`}>{SUBMITTAL_STATUS_LABELS[item.status]}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Main content: 2/3 + 1/3 on desktop ───────────────────── */}
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
          {/* Left column */}
          <div className="min-w-0 space-y-4 sm:space-y-6 lg:col-span-2">
            {/* Details */}
            <Card data-tour-id="submittal-detail-metadata">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                  <div>
                    <dt className="font-medium text-muted-foreground">Priority</dt>
                    <dd className="mt-1">
                      <Badge variant="outline" className={PRIORITY_BADGE[item.priority]}>
                        {SUBMITTAL_PRIORITY_LABELS[item.priority]}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Type</dt>
                    <dd className="mt-0.5">{SUBMITTAL_TYPE_LABELS[item.submittal_type]}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Package</dt>
                    <dd className="mt-0.5">{item.package?.package_id || 'Unpackaged'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Spec Section</dt>
                    <dd className="mt-0.5">{item.specification_section || '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Contractor</dt>
                    <dd className="mt-0.5">{item.responsible_contractor || '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Current Reviewer</dt>
                    <dd className="mt-0.5">{userFullName(item.ball_in_court_user)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Required Response
                    </dt>
                    <dd className={`mt-0.5 ${item.is_overdue ? 'font-semibold text-red-600' : ''}`}>
                      {formatDate(item.required_response_date)}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Days Open</dt>
                    <dd className="mt-0.5">{item.days_open}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Submitted By</dt>
                    <dd className="mt-0.5">{userFullName(item.submitted_by)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Submittal Manager</dt>
                    <dd className="mt-0.5">{userFullName(item.submittal_manager)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Created At
                    </dt>
                    <dd className="mt-0.5">{formatDateTime(item.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Revision</dt>
                    <dd className="mt-0.5">Rev {item.revision_number}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                {item.description ? (
                  <p className="text-sm whitespace-pre-wrap">{item.description}</p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No description provided.</p>
                )}
              </CardContent>
            </Card>

            {/* Workflow Step */}
            <Card data-tour-id="submittal-detail-workflow">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ListChecks className="h-5 w-5" />
                  Workflow Step
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!currentStep ? (
                  <p className="text-sm text-muted-foreground">Workflow complete.</p>
                ) : (
                  <>
                    <div className="text-sm">
                      Step {currentStep.step_number} {currentStep.title ? `- ${currentStep.title}` : ''}
                    </div>
                    <div className="space-y-2">
                      {currentStep.reviewers.map((reviewer) => (
                        <div key={reviewer.id} className="border rounded-md p-2">
                          <p className="text-sm font-medium">
                            {reviewer.reviewer
                              ? `${reviewer.reviewer.first_name} ${reviewer.reviewer.last_name}`
                              : reviewer.reviewer_role || 'Reviewer'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {reviewer.is_required ? 'Required' : 'Optional'} •{' '}
                            {reviewer.has_responded ? `Responded ${formatDateTime(reviewer.responded_at)}` : 'Pending'}
                          </p>
                        </div>
                      ))}
                    </div>
                    {pendingReviewers.length > 0 && (
                      <div className="space-y-2 pt-2 border-t">
                        <Select value={reviewerAssignmentId} onValueChange={setReviewerAssignmentId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select reviewer" />
                          </SelectTrigger>
                          <SelectContent>
                            {pendingReviewers.map((reviewer) => (
                              <SelectItem key={reviewer.id} value={reviewer.id}>
                                {reviewer.reviewer
                                  ? `${reviewer.reviewer.first_name} ${reviewer.reviewer.last_name}`
                                  : reviewer.reviewer_role || 'Reviewer'}
                                {reviewer.is_required ? ' (Required)' : ' (Optional)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={responseCode} onValueChange={(value) => setResponseCode(value as SubmittalResponseCode)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(Object.keys(SUBMITTAL_RESPONSE_LABELS) as SubmittalResponseCode[]).map((code) => (
                              <SelectItem key={code} value={code}>
                                {SUBMITTAL_RESPONSE_LABELS[code]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Textarea
                          value={responseComment}
                          onChange={(e) => setResponseComment(e.target.value)}
                          placeholder="Reviewer response comments..."
                        />
                        <Button
                          size="sm"
                          className="w-full sm:w-auto"
                          onClick={async () => {
                            if (!id) return;
                            if (!reviewerAssignmentId) return;
                            await respondItem.mutateAsync({
                              id,
                              reviewer_assignment: reviewerAssignmentId,
                              response_code: responseCode,
                              comment: responseComment,
                            });
                            setResponseComment('');
                          }}
                          disabled={!reviewerAssignmentId}
                        >
                          Submit Response
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Close & Distribute */}
            <Card data-tour-id="submittal-detail-close">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Close &amp; Distribute
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Current status: {SUBMITTAL_STATUS_LABELS[item.status]}
                </p>
                <Select value={closeCode} onValueChange={(value) => setCloseCode(value as SubmittalResponseCode)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(SUBMITTAL_RESPONSE_LABELS) as SubmittalResponseCode[]).map((code) => (
                      <SelectItem key={code} value={code}>
                        {SUBMITTAL_RESPONSE_LABELS[code]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="space-y-2 border rounded-md p-3 max-h-36 overflow-y-auto">
                  {users.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No recipients available.</p>
                  ) : (
                    users.map((user) => {
                      const checked = distributionIds.includes(user.id);
                      return (
                        <div key={user.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`submittal-dist-${user.id}`}
                            checked={checked}
                            onCheckedChange={() =>
                              setDistributionIds((prev) =>
                                prev.includes(user.id) ? prev.filter((idValue) => idValue !== user.id) : [...prev, user.id]
                              )
                            }
                          />
                          <label htmlFor={`submittal-dist-${user.id}`} className="text-sm cursor-pointer">
                            {user.first_name} {user.last_name}
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
                <Textarea
                  value={closeComment}
                  onChange={(e) => setCloseComment(e.target.value)}
                  placeholder="Closing comments..."
                />
                <Button
                  className="w-full sm:w-auto"
                  onClick={async () => {
                    if (!id) return;
                    await closeItem.mutateAsync({
                      id,
                      final_response_code: closeCode,
                      close_comments: closeComment,
                      distribution_recipient_ids: distributionIds,
                    });
                  }}
                  disabled={item.status === 'CLOSED'}
                >
                  Close Submittal
                </Button>
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card data-tour-id="submittal-detail-attachments">
              <CardHeader className="gap-3 p-4 pb-2 sm:flex-row sm:items-center sm:justify-between sm:p-6 sm:pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Paperclip className="h-5 w-5" />
                  Attachments ({item.attachments.length})
                </CardTitle>
                <Button size="sm" variant="outline" className="h-8 w-full sm:w-auto" onClick={() => fileInputRef.current?.click()}>
                  <Paperclip className="mr-1.5 h-3.5 w-3.5" />
                  Attach File
                </Button>
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
              </CardHeader>
              <CardContent className="space-y-2">
                {item.attachments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No attachments.</p>
                ) : (
                  item.attachments.map((attachment) => (
                    <div key={attachment.id} className="flex min-w-0 items-center justify-between gap-2 rounded-md border p-2">
                      <a
                        href={attachment.file_url ?? attachment.file}
                        target="_blank"
                        rel="noreferrer"
                        className="min-w-0 flex-1 truncate text-sm text-primary hover:underline"
                      >
                        {attachment.file.split('/').pop()}
                      </a>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="shrink-0"
                        onClick={() => id && deleteAttachment.mutate({ id, attachmentId: attachment.id })}
                      >
                        Remove
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div className="min-w-0 space-y-4 sm:space-y-6 lg:col-span-1">
            {/* Revisions */}
            <Card data-tour-id="submittal-detail-revisions">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Revisions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {revisions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No other revisions.</p>
                ) : (
                  revisions.map((revision) => (
                    <div
                      key={revision.id}
                      className={`border rounded-md p-2 text-sm cursor-pointer hover:bg-muted/50 ${revision.id === item.id ? 'border-primary' : ''}`}
                      onClick={() => navigate(`${backPath}/${revision.id}`)}
                    >
                      <p className="font-medium">
                        {revision.submittal_number} - Rev {revision.revision_number}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {SUBMITTAL_STATUS_LABELS[revision.status]} • {formatDateTime(revision.created_at)}
                      </p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Status History */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Status History
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {item.status_history.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No history yet.</p>
                ) : (
                  item.status_history.map((entry) => (
                    <div key={entry.id} className="text-sm border rounded-md p-2">
                      <p className="font-medium">
                        {entry.from_status || 'Created'} → {entry.to_status}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {entry.note || 'Status updated'} • {formatDateTime(entry.created_at)}
                      </p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Audit Trail */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  Audit Trail
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {item.audit_logs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No audit entries yet.</p>
                ) : (
                  item.audit_logs.map((entry) => (
                    <div key={entry.id} className="text-sm border rounded-md p-2">
                      <p className="font-medium">{entry.message}</p>
                      <p className="text-xs text-muted-foreground">{formatDateTime(entry.created_at)}</p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <SubmittalFormDialog
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        projectId={numericProjectId}
        item={item}
      />
    </BaseLayout>
  );
};
